import { useEffect, useRef } from 'react';
import type { ExternalUserStatus } from '../context/ExternalUserContext';
import type { PunchFlowState } from '../components/PunchPad/punchFlowReducer';

type Phase = PunchFlowState['phase'];

// Punch-flow phases with an in-flight, un-cancelable network write. A POS
// takeover must never interrupt these — it waits until the write settles so
// the punch is saved before switching users (API-4792).
const WRITE_PHASES: ReadonlySet<Phase> = new Set<Phase>([
    'loading_status',
    'executing_action'
]);

export interface UseExternalTakeoverParams {
    /** Whether an external user is currently set (externalUserId !== null). */
    isExternalUserMode: boolean;
    /** Resolution status of the current external-user request. */
    externalStatus: ExternalUserStatus;
    /** Resolved employee (PIN) once status is `resolved`. */
    resolvedEmployeeId: string | null;
    /** Monotonic id of the current external-user request (bumped per message). */
    requestId: number;
    /** Current punch-flow phase. */
    phase: Phase;
    /** Reset the punch flow to idle (dispatch DISMISS). Must be stable. */
    resetToIdle: () => void;
    /** Feed a resolved employee ID into the punch flow (auto-skip the PIN pad). */
    submitResolvedPin: (employeeId: string) => void;
}

export interface ExternalTakeoverRender {
    /**
     * A new external request is taking over a stable (non-write, non-idle)
     * session. The caller should show a spinner instead of briefly painting the
     * outgoing user's screen before the reset-to-idle lands.
     */
    takeoverPending: boolean;
    /** Resolved and about to auto-skip from idle (mapping done, PIN not yet fed). */
    autoSkipPending: boolean;
    /** Post-auto-skip status fetch in flight (new user's `loading_status`). */
    awaitingStatusAfterAutoSkip: boolean;
}

/**
 * Coordinates external-user takeover of the punch flow.
 *
 * The POS is authoritative about who is at the terminal, so a `set_external_user`
 * message always takes over — except it never interrupts an in-flight punch
 * write. Behavior by current phase when a new request arrives:
 *   - write phase (`loading_status`/`executing_action`) → defer; the effect
 *     re-runs when `phase` changes and completes once the write settles.
 *   - other non-idle phase → reset to idle so the auto-skip can switch users.
 *   - idle → auto-skip feeds the resolved employee straight into the flow.
 *
 * Extracted from PunchPadPage so the coordination logic is unit-testable
 * without mounting the full page (API-4792).
 */
export function useExternalTakeover({
    isExternalUserMode,
    externalStatus,
    resolvedEmployeeId,
    requestId,
    phase,
    resetToIdle,
    submitResolvedPin
}: UseExternalTakeoverParams): ExternalTakeoverRender {
    // The external request we've already driven a takeover for (dismissed a
    // stable session to idle, or found already idle) — prevents re-dismissing.
    const takeoverHandledRef = useRef<number | null>(null);
    // The external request we've already auto-skipped (fed into the punch flow).
    const autoSkippedForRequestRef = useRef<number | null>(null);

    if (!isExternalUserMode) {
        takeoverHandledRef.current = null;
        autoSkippedForRequestRef.current = null;
    }

    // Takeover coordinator: clear the outgoing session so auto-skip can switch
    // to the new user, but defer while a punch write is in flight.
    useEffect(() => {
        if (!isExternalUserMode) return;
        if (takeoverHandledRef.current === requestId) return;
        if (WRITE_PHASES.has(phase)) return; // defer until the write settles
        if (phase !== 'idle') {
            resetToIdle();
        }
        takeoverHandledRef.current = requestId;
    }, [isExternalUserMode, requestId, phase, resetToIdle]);

    // Auto-skip: once the mapping resolves and the flow is idle, feed the
    // employee ID in so the user lands on their status page. Keyed on requestId
    // (not the employee value) so a repeat set_external_user still skips once.
    useEffect(() => {
        const willAutoSkip =
            isExternalUserMode &&
            externalStatus === 'resolved' &&
            resolvedEmployeeId !== null &&
            phase === 'idle' &&
            autoSkippedForRequestRef.current !== requestId;
        if (willAutoSkip) {
            autoSkippedForRequestRef.current = requestId;
            submitResolvedPin(resolvedEmployeeId);
        }
    }, [
        isExternalUserMode,
        externalStatus,
        resolvedEmployeeId,
        phase,
        requestId,
        submitResolvedPin
    ]);

    const takeoverPending =
        isExternalUserMode &&
        phase !== 'idle' &&
        !WRITE_PHASES.has(phase) &&
        takeoverHandledRef.current !== requestId;

    const autoSkipPending =
        isExternalUserMode &&
        externalStatus === 'resolved' &&
        phase === 'idle' &&
        autoSkippedForRequestRef.current !== requestId;

    const awaitingStatusAfterAutoSkip =
        isExternalUserMode &&
        externalStatus === 'resolved' &&
        phase === 'loading_status';

    return { takeoverPending, autoSkipPending, awaitingStatusAfterAutoSkip };
}
